import { gql } from '@soundxyz/gql-string';
import { MintTimeStatusEnum, ReleaseAlbumRevealFilterOptionEnum } from '@soundxyz/utils/const';
import type { ReleasesCursorFilterArgs } from '@graphql/reactQuery';
import { SortOrder, UpcomingDropsPageDocument, useInfiniteQuery } from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  query UpcomingDropsPage(
    $pagination: ReleasesCursorConnectionArgs
    $filter: ReleasesCursorFilterArgs
  ) {
    releases(pagination: $pagination, filter: $filter) {
      edges {
        node {
          id
          mintStartTime
          track {
            id
          }
          ...UpcomingDropCard
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

export const BASE_UPCOMING_FILTER = {
  mintTimeStatus: [MintTimeStatusEnum.UPCOMING],
  releaseAlbumRevealStatus: ReleaseAlbumRevealFilterOptionEnum.ONLY_NOT_REVEALED_ALBUMS,
} satisfies ReleasesCursorFilterArgs;

export const INITIAL_PAGE_SIZE = 16;

export function useUpcomingDrops({
  limit = INITIAL_PAGE_SIZE,
  filter,
}: {
  limit?: number;
  filter: ReleasesCursorFilterArgs;
}) {
  const {
    orderedList: upcomingDrops,
    loadMoreNextPage: loadMore,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage = false,
  } = useInfiniteQuery(UpcomingDropsPageDocument, {
    filterQueryKey: {
      filter,
    },
    getNextPageParam({ data }) {
      return (
        data.releases.pageInfo.hasNextPage && {
          after: data.releases.pageInfo.endCursor,
        }
      );
    },
    variables({ pageParam }) {
      return {
        pagination: {
          first: limit ?? INITIAL_PAGE_SIZE,
          after: pageParam?.after || null,
          sort: {
            mintStartTime: SortOrder.Asc,
          },
        },
        filter,
      };
    },
    list({ releases }) {
      return releases.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  return {
    upcomingDrops,
    loadMore,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
  };
}
